// @flow

import type {EmbedPlatform, EmbedType, EmbedValidationResult} from './types';

export function detectEmbedType(url: string): EmbedPlatform | null {
  if (!url) return null;

  const normalizedUrl = url.toLowerCase();

  if (
    normalizedUrl.includes('youtube.com') ||
    normalizedUrl.includes('youtu.be')
  ) {
    return 'youtube';
  }

  if (normalizedUrl.includes('instagram.com')) {
    return 'instagram';
  }

  if (
    normalizedUrl.includes('facebook.com') ||
    normalizedUrl.includes('fb.watch')
  ) {
    return 'facebook';
  }

  if (normalizedUrl.includes('vimeo.com')) {
    return 'vimeo';
  }

  if (normalizedUrl.includes('tiktok.com')) {
    return 'tiktok';
  }

  return 'generic';
}

export function validateUrl(url: string): EmbedValidationResult {
  if (!url) {
    return {isValid: false, error: 'URL is required'};
  }

  try {
    new URL(url);
  } catch {
    return {isValid: false, error: 'Invalid URL format'};
  }

  const platform = detectEmbedType(url);
  if (!platform) {
    return {isValid: false, error: 'Unsupported platform'};
  }

  return {isValid: true, platform};
}

export function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

export function getYouTubeThumbnail(
  videoId: string,
  quality: 'default' | 'hqdefault' | 'sddefault' | 'maxresdefault' = 'hqdefault'
): string {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

export function getEmbedDimensions(
  platform: EmbedPlatform,
  type?: EmbedType
): {width: number, height: number} {
  const defaultDimensions = {width: 560, height: 315};

  switch (platform) {
    case 'youtube':
      return defaultDimensions;
    case 'instagram':
      return {width: 540, height: 540};
    case 'facebook':
      return {width: 500, height: 600};
    case 'vimeo':
      return defaultDimensions;
    case 'tiktok':
      return {width: 325, height: 575};
    default:
      return defaultDimensions;
  }
}
